import { motion } from "framer-motion";
import {
  CheckSquare, DollarSign, TrendingUp, TrendingDown,
  Bell, Clock, AlertTriangle, ListTodo, Target,
  FileText, FolderKanban, Zap, Wallet, Calendar,
  Check, CheckCheck
} from "lucide-react";
import { format, parseISO, isPast, isToday, isTomorrow } from "date-fns";
import { ptBR } from "date-fns/locale";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ChatHeader, TimelineList, CategoryBars, MoneyStrip,
  AchievementCard, SmartSuggestion, QuickActions, StatusPill
} from "@/components/chat/premium";
import MessageDataCard from "@/components/chat/MessageDataCard";

// REFINO-pos-ec7fb7d (D5, 2026-06-03): componentes para evitar que o
// markdown renderize tags pesadas (h1/h2/h3) ou bullets com style padrão.
// Bold/itálico via **/`_` viram <strong>/<em> normalmente.
const _MD_COMPONENTS = {
  // Strip headings — agent loop emite "### Tarefas" cru. Vira parágrafo
  // normal pra não destacar visualmente.
  h1: ({ children }) => <p className="font-semibold">{children}</p>,
  h2: ({ children }) => <p className="font-semibold">{children}</p>,
  h3: ({ children }) => <p className="font-semibold">{children}</p>,
  h4: ({ children }) => <p className="font-semibold">{children}</p>,
  // Lists: render normal but tighten spacing pra caber no balão.
  ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 my-1">{children}</ol>,
  // Paragraph: zero margin entre — espaçamento controlado pelo whitespace
  // do msg.text.
  p: ({ children }) => <p className="my-0">{children}</p>,
};

function _renderMessageText(text) {
  if (!text) return null;
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={_MD_COMPONENTS}>
      {text}
    </ReactMarkdown>
  );
}

// ===== Helpers compartilhados pelas cards Fase G =====
const CATEGORY_CLASS_MAP = {
  alimentacao:  "bg-category-food",
  comida:       "bg-category-food",
  ifood:        "bg-category-food",
  restaurante:  "bg-category-food",
  transporte:   "bg-category-transport",
  uber:         "bg-category-transport",
  combustivel:  "bg-category-transport",
  lazer:        "bg-category-leisure",
  entretenimento: "bg-category-leisure",
  saude:        "bg-category-health",
  farmacia:     "bg-category-health",
  moradia:      "bg-category-housing",
  aluguel:      "bg-category-housing",
  conta:        "bg-category-housing",
  salario:      "bg-semantic-success",
};
const categoryClass = (name) => {
  if (!name) return "bg-category-other";
  const k = name.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  return CATEGORY_CLASS_MAP[k] || "bg-category-other";
};

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
const fmtDate = (d) => {
  if (!d) return "";
  try {
    const date = typeof d === "string" ? parseISO(d) : new Date(d);
    if (isToday(date)) return "Hoje";
    if (isTomorrow(date)) return "Amanhã";
    return format(date, "dd/MM", { locale: ptBR });
  } catch { return ""; }
};
const fmtTime = (d) => {
  if (!d) return "";
  try { return format(parseISO(d), "HH:mm"); } catch { return ""; }
};
const safeIsPast = (iso) => { try { return isPast(parseISO(iso)); } catch { return false; } };

// ========== TASKS CARD (refactor Fase G — DS premium) ==========
const TasksCard = ({ data }) => {
  const raw = data?.tasks || data || [];
  const tasks = Array.isArray(raw) ? raw : [];
  const count = data?.count || tasks.length;
  const overdueCount = tasks.filter(
    (t) => t.datetime_due && !t.is_completed && t.status !== "done" && safeIsPast(t.datetime_due)
  ).length;
  const proactiveHint = data?.proactive_hint
    || (overdueCount > 0
      ? `Tem ${overdueCount} tarefa${overdueCount !== 1 ? "s" : ""} atrasada${overdueCount !== 1 ? "s" : ""}. Quer adiar?`
      : null);

  const items = tasks.slice(0, 8).map((t) => {
    const isOverdue = t.datetime_due && !t.is_completed && t.status !== "done" && safeIsPast(t.datetime_due);
    const done = t.is_completed || t.status === "done";
    const prio = t.priority || "low";
    const prioLabel = prio === "high" ? "Alta" : prio === "urgent" ? "Urgente" : null;
    return {
      id: t.id,
      time: t.datetime_due ? `${fmtDate(t.datetime_due)} ${fmtTime(t.datetime_due)}`.trim() : null,
      title: t.title,
      subtitle: t.project_name ? `Projeto: ${t.project_name}` : null,
      status: done ? "success" : isOverdue ? "danger" : "info",
      done,
      badge: prioLabel,
    };
  });

  return (
    <div className="p-3 rounded-xl border border-brand-cyan/15 bg-nectar-card/40" data-testid="tasks-data-card">
      <ChatHeader label={`TAREFAS · ${count} ${count === 1 ? "ITEM" : "ITENS"}`} />
      <TimelineList items={items} />
      {proactiveHint ? (
        <div className="mt-3"><SmartSuggestion tone="warning" message={proactiveHint} /></div>
      ) : null}
    </div>
  );
};

// ========== TRANSACTIONS CARD (refactor Fase G) ==========
const TransactionsCard = ({ data, title }) => {
  const raw = data?.transactions || data || [];
  const txs = Array.isArray(raw) ? raw : [];
  const sorted = [...txs].sort((a, b) => {
    const da = new Date(a.date || a.created_at || 0).getTime();
    const db_ = new Date(b.date || b.created_at || 0).getTime();
    return db_ - da;
  });
  const expenses = txs.filter((t) => t.type === "expense");
  const incomes = txs.filter((t) => t.type === "income");
  const balance = incomes.reduce((s, t) => s + (t.amount || 0), 0)
    - expenses.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="p-3 rounded-xl border border-semantic-success/15 bg-nectar-card/40" data-testid="transactions-data-card">
      <ChatHeader
        label={(title || `TRANSAÇÕES · ${txs.length} ${txs.length === 1 ? "ITEM" : "ITENS"}`).toUpperCase()}
        meta={txs.length ? `Saldo ${fmt(balance)}` : null}
      />
      {sorted.length === 0 ? (
        <p className="text-[11px] text-slate-500 text-center py-3">Nenhuma transação</p>
      ) : (
        <div className="space-y-1">
          {sorted.slice(0, 8).map((tx, i) => (
            <MoneyStrip
              key={tx.id || i}
              label={(tx.category || "outros").toUpperCase()}
              description={tx.description || tx.category || "Transação"}
              amount={tx.amount}
              type={tx.type === "income" ? "income" : "expense"}
              accent={categoryClass(tx.category)}
              meta={fmtDate(tx.date || tx.created_at)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ========== FINANCE SUMMARY CARD (refactor Fase G) ==========
const FinanceSummaryCard = ({ data }) => {
  const income = data?.total_income || data?.income || 0;
  const expenses = data?.total_expenses || data?.expenses || 0;
  const balance = data?.balance ?? (income - expenses);
  const byCategory = data?.by_category || {};
  const period = data?.period || data?.month_label || "PERÍODO";
  const previous = data?.previous_period_total;
  const pctChanges = data?.categories_pct_change || {};

  const totalForBars = Object.values(byCategory).reduce((s, v) => s + Number(v || 0), 0);
  const categories = Object.entries(byCategory).map(([name, value]) => ({
    name,
    value: Number(value || 0),
    color: categoryClass(name),
    total: totalForBars,
  }));

  let variationLabel = null;
  let variationTone = "neutral";
  if (typeof previous === "number" && previous > 0) {
    const delta = expenses - previous;
    const pct = Math.round((delta / previous) * 100);
    variationTone = pct > 0 ? "danger" : pct < 0 ? "success" : "neutral";
    variationLabel = `${pct > 0 ? "+" : ""}${pct}% vs período anterior`;
  }

  const spike = Object.entries(pctChanges).find(([_, p]) => Number(p) >= 20);
  const spikeMessage = spike
    ? `Gasto em ${spike[0]} subiu ${Math.round(spike[1])}% vs período anterior. Quer revisar?`
    : null;

  return (
    <div className="p-3 rounded-xl border border-brand-purple/15 bg-nectar-card/40" data-testid="finance-summary-card">
      <ChatHeader label={`DESPESAS · ${period.toString().toUpperCase()}`} />

      <div className="flex items-baseline justify-between gap-3 mt-1 mb-3">
        <p className="text-2xl font-bold tabular-nums text-slate-100">{fmt(expenses)}</p>
        {variationLabel ? <StatusPill tone={variationTone}>{variationLabel}</StatusPill> : null}
      </div>

      {balance !== 0 || income > 0 ? (
        <p className={`text-[11px] tabular-nums ${balance >= 0 ? "text-semantic-success" : "text-semantic-danger"} mb-3`}>
          Saldo {fmt(balance)} · Entrou {fmt(income)}
        </p>
      ) : null}

      {categories.length > 0 ? (
        <CategoryBars categories={categories} />
      ) : (
        <p className="text-[11px] text-slate-500 text-center py-3">Sem categorias</p>
      )}

      {spikeMessage ? (
        <div className="mt-3"><SmartSuggestion tone="warning" message={spikeMessage} /></div>
      ) : null}
    </div>
  );
};

// ========== REMINDERS CARD (refactor Fase G) ==========
const RemindersCard = ({ data }) => {
  const raw = data?.reminders || data || [];
  const rems = Array.isArray(raw) ? raw : [];

  const items = rems.slice(0, 8).map((r) => {
    const overdue = r.datetime && !r.is_completed && safeIsPast(r.datetime);
    const done = r.is_completed === true;
    return {
      id: r.id,
      time: r.datetime ? `${fmtDate(r.datetime)} ${fmtTime(r.datetime)}`.trim() : null,
      title: r.message,
      status: done ? "success" : overdue ? "danger" : "warning",
      done,
      badge: overdue ? "Atrasado" : null,
    };
  });

  return (
    <div className="p-3 rounded-xl border border-brand-cyan/15 bg-nectar-card/40" data-testid="reminders-data-card">
      <ChatHeader label={`LEMBRETES · ${rems.length} ${rems.length === 1 ? "ITEM" : "ITENS"}`} />
      <TimelineList items={items} />
    </div>
  );
};

// ========== HABITS CARD (refactor Fase G) ==========
const HabitsCard = ({ data }) => {
  const raw = data?.habits || data || [];
  const habits = Array.isArray(raw) ? raw : [];
  const checkedToday = habits.filter((h) => h.checked_today).length;
  const allDone = habits.length > 0 && checkedToday === habits.length;

  return (
    <div className="p-3 rounded-xl border border-brand-green/15 bg-nectar-card/40" data-testid="habits-data-card">
      <ChatHeader
        label={`HÁBITOS · ${habits.length} ${habits.length === 1 ? "ITEM" : "ITENS"}`}
        meta={habits.length ? `${checkedToday}/${habits.length} hoje` : null}
      />

      {allDone ? (
        <div className="my-3">
          <AchievementCard
            title="Sequência de hábitos completa"
            subtitle={`${habits.length}/${habits.length} hoje — você cravou`}
          />
        </div>
      ) : null}

      {habits.length === 0 ? (
        <p className="text-[11px] text-slate-500 text-center py-3">Nenhum hábito</p>
      ) : (
        <div className="space-y-1.5">
          {habits.slice(0, 8).map((h, i) => (
            <div key={h.id || i} className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-white/[0.02]">
              <Target className={`w-4 h-4 shrink-0 ${h.checked_today ? "text-semantic-success" : "text-slate-500"}`} />
              <p className={`flex-1 text-sm truncate ${h.checked_today ? "text-slate-200" : "text-slate-300"}`}>
                {h.name}
              </p>
              {h.streak > 0 ? (
                <StatusPill tone="warning">{h.streak}🔥</StatusPill>
              ) : null}
              {h.checked_today ? <CheckCheck className="w-4 h-4 text-semantic-success shrink-0" /> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== DAILY DIGEST CARD (refactor Fase G — substitui DashboardSummaryCard) ==========
const greetingByHour = (h) => {
  if (h < 6)  return "Boa madrugada";
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
};

const DailyDigestCard = ({ data }) => {
  const now = new Date();
  const greeting = greetingByHour(now.getHours());
  const userName = data?.user_name || "Mestre";
  const dateLabel = format(now, "d 'de' MMMM", { locale: ptBR }).toUpperCase();

  // Mescla tarefas + lembretes em timeline ordenada por hora
  const todayTasks = (data?.today_tasks || []).map((t) => ({
    id: `task-${t.id || t.title}`,
    time: t.datetime_due ? fmtTime(t.datetime_due) : null,
    title: t.title,
    status: (t.is_completed || t.status === "done") ? "success"
      : (t.datetime_due && safeIsPast(t.datetime_due)) ? "danger" : "info",
    done: t.is_completed || t.status === "done",
    badge: "Tarefa",
    _ts: t.datetime_due ? new Date(t.datetime_due).getTime() : Infinity,
  }));
  const todayReminders = (data?.today_reminders || []).map((r) => ({
    id: `rem-${r.id || r.message}`,
    time: r.datetime ? fmtTime(r.datetime) : null,
    title: r.message,
    status: (r.datetime && safeIsPast(r.datetime) && !r.is_completed) ? "danger" : "warning",
    badge: "Lembrete",
    _ts: r.datetime ? new Date(r.datetime).getTime() : Infinity,
  }));
  const timeline = [...todayTasks, ...todayReminders].sort((a, b) => a._ts - b._ts);

  // Resumo financeiro do dia (ou mês como fallback)
  const todayBalance = data?.today_balance ?? data?.month_balance;
  const balanceLabel = data?.today_balance !== undefined ? "SALDO HOJE" : "SALDO DO MÊS";

  // Conquistas: projetos 100% concluídos
  const completedProjects = data?.completed_projects || [];

  // Smart suggestion proativa
  const taskWithoutReminder = todayTasks.find(
    (t) => !t.done && t.time && !todayReminders.some((r) => r.time === t.time)
  );
  const hint = data?.proactive_hint
    || (taskWithoutReminder
      ? `Posso te lembrar 5min antes da tarefa "${taskWithoutReminder.title.slice(0, 40)}"?`
      : (data?.tasks_overdue > 0
        ? `Tem ${data.tasks_overdue} tarefa${data.tasks_overdue !== 1 ? "s" : ""} atrasada${data.tasks_overdue !== 1 ? "s" : ""}. Quer adiar?`
        : null));

  // Resumo numérico (legado — fallback quando today_tasks/reminders ausentes)
  const showLegacyGrid = timeline.length === 0 && (
    data?.tasks_total || data?.tasks_overdue || data?.reminders_today
    || data?.month_balance != null || data?.habits_total
  );
  const legacyItems = [
    { label: "TAREFAS", value: data?.tasks_total || 0, color: "text-brand-cyan" },
    { label: "ATRASADAS", value: data?.tasks_overdue || 0, color: data?.tasks_overdue > 0 ? "text-semantic-danger" : "text-slate-400" },
    { label: "LEMBRETES", value: data?.reminders_today || 0, color: "text-brand-purple" },
    { label: "HÁBITOS", value: `${data?.habits_checked || 0}/${data?.habits_total || 0}`, color: "text-semantic-success" },
  ].filter((i) => i.value !== 0 || typeof i.value === "string");

  return (
    <div className="p-3 rounded-xl border border-brand-purple/15 bg-nectar-card/40" data-testid="dashboard-summary-card">
      <ChatHeader
        label={`${greeting.toUpperCase()}, ${userName.toUpperCase()}`}
        meta={dateLabel}
      />

      {/* Conquistas em destaque */}
      {completedProjects.slice(0, 2).map((p, i) => (
        <div key={`achv-${i}`} className="my-2">
          <AchievementCard title={p.name || p} subtitle="Projeto concluído" progress={100} />
        </div>
      ))}

      {/* Timeline mesclada de tarefas + lembretes do dia */}
      {timeline.length > 0 ? (
        <TimelineList items={timeline} />
      ) : showLegacyGrid ? (
        <div className="grid grid-cols-2 gap-2 my-2">
          {legacyItems.map((item, i) => (
            <div key={i} className="flex items-baseline justify-between p-2 rounded-md bg-white/[0.02]">
              <span className="text-[10px] tracking-[0.1em] uppercase text-slate-500">{item.label}</span>
              <span className={`text-sm font-semibold ${item.color}`}>{item.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-slate-500 text-center py-3">Dia vazio. Boa hora pra planejar.</p>
      )}

      {/* Resumo financeiro */}
      {todayBalance != null ? (
        <div className="mt-2 mb-3">
          <MoneyStrip
            label={balanceLabel}
            description="Movimento líquido"
            amount={Math.abs(todayBalance)}
            type={todayBalance >= 0 ? "income" : "expense"}
          />
        </div>
      ) : null}

      {/* Insights legados */}
      {data?.insights?.length > 0 ? (
        <div className="mt-2 mb-3 space-y-1">
          {data.insights.map((insight, i) => (
            <p key={i} className="text-[12px] text-slate-400 leading-snug pl-2 border-l border-brand-purple/30">
              {insight}
            </p>
          ))}
        </div>
      ) : null}

      {/* Smart suggestion proativa */}
      {hint ? (
        <div className="mt-2 mb-3"><SmartSuggestion tone="info" message={hint} /></div>
      ) : null}

      {/* Quick actions */}
      <QuickActions
        actions={[
          { label: "adicionar", onClick: () => {} },
          { label: "ver agenda", onClick: () => {} },
          { label: "ler em voz", onClick: () => {} },
        ]}
      />
    </div>
  );
};

// ========== NOTES CARD (refactor Fase G) ==========
const NotesCard = ({ data }) => {
  const notes = data?.notes || [];

  return (
    <div className="p-3 rounded-xl border border-amber-500/15 bg-nectar-card/40" data-testid="notes-data-card">
      <ChatHeader label={`NOTAS · ${notes.length} ${notes.length === 1 ? "ITEM" : "ITENS"}`} />
      {notes.length === 0 ? (
        <p className="text-[11px] text-slate-500 text-center py-3">Nenhuma nota</p>
      ) : (
        <div className="divide-y divide-white/5">
          {notes.slice(0, 6).map((n, i) => (
            <div key={i} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-[10px] tracking-[0.1em] uppercase text-slate-500">
                  {fmtDate(n.created_at)}
                </span>
                {n.tag ? <StatusPill tone="neutral">{n.tag}</StatusPill> : null}
              </div>
              <p className="text-sm text-slate-300 line-clamp-2 leading-snug">{n.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== PROGRESS RING (SVG inline) ==========
function ProgressRing({ value = 0, size = 28, stroke = 3 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, value));
  const offset = c - (pct / 100) * c;
  const isComplete = pct >= 100;
  const color = isComplete ? "text-semantic-success" : "text-brand-purple";
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={`${color} shrink-0`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeOpacity="0.2" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke="currentColor" strokeWidth={stroke} fill="none"
        strokeDasharray={c} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" textAnchor="middle" dy=".35em" className="fill-current" style={{ fontSize: 9, fontWeight: 600 }}>
        {pct}
      </text>
    </svg>
  );
}

// ========== PROJECTS CARD (refactor Fase G) ==========
const ProjectsCard = ({ data }) => {
  const projects = data?.projects || [];
  const completed = projects.filter((p) => (p.progress ?? 0) >= 100);

  return (
    <div className="p-3 rounded-xl border border-brand-purple/15 bg-nectar-card/40" data-testid="projects-data-card">
      <ChatHeader label={`PROJETOS · ${projects.length} ${projects.length === 1 ? "ITEM" : "ITENS"}`} />

      {completed.slice(0, 1).map((p, i) => (
        <div key={`done-${i}`} className="my-3">
          <AchievementCard title={p.name} subtitle="Projeto concluído" progress={100} />
        </div>
      ))}

      {projects.length === 0 ? (
        <p className="text-[11px] text-slate-500 text-center py-3">Nenhum projeto</p>
      ) : (
        <div className="space-y-1.5">
          {projects.slice(0, 6).map((p, i) => {
            const progress = typeof p.progress === "number"
              ? p.progress
              : (p.task_count ? Math.round(((p.completed_count || 0) / p.task_count) * 100) : 0);
            return (
              <div key={p.id || i} className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-white/[0.02]">
                <ProgressRing value={progress} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200 truncate">{p.name}</p>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                    {p.task_count != null ? <span>{p.task_count} tarefas</span> : null}
                    {p.deadline ? <><Calendar className="w-3 h-3" /><span>{fmtDate(p.deadline)}</span></> : null}
                  </div>
                </div>
                {p.status === "active" ? <StatusPill tone="success">Ativo</StatusPill> : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ========== AGENDA CARD (Fase G — calendar.list / agenda.day) ==========
function _buildAgendaItems(events, gapMinThreshold = 30) {
  const sorted = [...events].sort((a, b) => {
    const ta = new Date(a.start || a.datetime || 0).getTime();
    const tb = new Date(b.start || b.datetime || 0).getTime();
    return ta - tb;
  });
  const items = [];
  for (let i = 0; i < sorted.length; i++) {
    const ev = sorted[i];
    items.push({
      id: ev.id || `ev-${i}`,
      time: fmtTime(ev.start || ev.datetime),
      title: ev.summary || ev.title || ev.message || "(sem título)",
      subtitle: ev.location || ev.description || null,
      status: "info",
      badge: ev.duration_min ? `${ev.duration_min}min` : null,
    });
    const next = sorted[i + 1];
    if (next) {
      const endA = new Date(ev.end || ev.start || ev.datetime).getTime();
      const startB = new Date(next.start || next.datetime).getTime();
      const gapMin = Math.round((startB - endA) / 60000);
      if (gapMin >= gapMinThreshold) {
        const gapH = Math.floor(gapMin / 60);
        const gapM = gapMin % 60;
        const label = gapH > 0
          ? `${gapH}h${gapM ? ` ${gapM}min` : ""} livres`
          : `${gapMin}min livres`;
        items.push({
          id: `gap-${i}`,
          type: "gap",
          title: label,
          subtitle: gapMin >= 60 ? "bom pra deep work" : null,
        });
      }
    }
  }
  return { items, sorted };
}

const AgendaCard = ({ data }) => {
  const events = data?.events || data?.agenda || data || [];
  const arr = Array.isArray(events) ? events : [];
  const dayLabel = data?.day_label
    || (data?.date ? format(parseISO(data.date), "EEEE", { locale: ptBR }) : "HOJE");
  const { items, sorted } = _buildAgendaItems(arr);

  // Total tempo livre em janelas >= 60min
  let totalDeepGapMin = 0;
  for (let i = 0; i < sorted.length - 1; i++) {
    const endA = new Date(sorted[i].end || sorted[i].start || 0).getTime();
    const startB = new Date(sorted[i + 1].start || sorted[i + 1].datetime || 0).getTime();
    const gapMin = Math.round((startB - endA) / 60000);
    if (gapMin >= 60) totalDeepGapMin += gapMin;
  }
  const deepGapHours = Math.floor(totalDeepGapMin / 60);
  const deepGapHint = deepGapHours >= 1
    ? `Quer bloquear ${deepGapHours}h livre${deepGapHours !== 1 ? "s" : ""} como deep work?`
    : null;

  return (
    <div className="p-3 rounded-xl border border-brand-cyan/15 bg-nectar-card/40" data-testid="agenda-data-card">
      <ChatHeader
        label={`AGENDA · ${dayLabel.toUpperCase()} · ${arr.length} ${arr.length === 1 ? "COMPROMISSO" : "COMPROMISSOS"}`}
      />
      {arr.length === 0 ? (
        <p className="text-[12px] text-slate-400 text-center py-4 italic">
          Dia livre. Sem compromissos.
        </p>
      ) : (
        <TimelineList items={items} />
      )}
      {deepGapHint ? (
        <div className="mt-3"><SmartSuggestion tone="info" message={deepGapHint} /></div>
      ) : null}
    </div>
  );
};

// ========== MAIN ACTION CARD RENDERER ==========
export function ActionDataCard({ action }) {
  if (!action?.type || !action?.data) return null;

  const t = action.type;

  if (t === "task.list" || t === "task.prioritize") return <TasksCard data={action.data} />;
  if (t === "finance.list") return <TransactionsCard data={action.data} />;
  if (t === "finance.summary") return <FinanceSummaryCard data={action.data} />;
  if (t === "reminder.list") return <RemindersCard data={action.data} />;
  if (t === "habit.list") return <HabitsCard data={action.data} />;
  if (t === "note.list") return <NotesCard data={action.data} />;
  if (t === "project.list") return <ProjectsCard data={action.data} />;
  if (t === "dashboard.summary" || t === "daily.digest") return <DailyDigestCard data={action.data} />;
  if (t === "calendar.list" || t === "agenda.day") return <AgendaCard data={action.data} />;

  return null;
}

// ========== STATUS ICON (✓ / ✓✓) ==========
function StatusIcon({ status }) {
  const s = status || "delivered";
  if (s === "sending") return <Check size={12} className="text-slate-500/60 animate-pulse" />;
  if (s === "sent")    return <Check size={12} className="text-slate-400" />;
  if (s === "read")    return <CheckCheck size={12} className="text-brand-cyan" />;
  return <CheckCheck size={12} className="text-slate-400" />;
}

// ========== TAIL (cauda do balão estilo WhatsApp) ==========
function Tail({ isUser }) {
  if (isUser) {
    return (
      <svg
        className="absolute bottom-0 -right-1.5 pointer-events-none"
        width="10" height="14" viewBox="0 0 10 14" aria-hidden="true"
      >
        <path d="M0 0 L0 14 L10 14 Z" fill="rgba(0, 212, 255, 0.10)" />
        <path d="M0 0 L10 14" stroke="rgba(0, 212, 255, 0.20)" strokeWidth="1" fill="none" />
      </svg>
    );
  }
  return (
    <svg
      className="absolute bottom-0 -left-1.5 pointer-events-none"
      width="10" height="14" viewBox="0 0 10 14" aria-hidden="true"
    >
      <path d="M10 0 L10 14 L0 14 Z" fill="#0d1220" />
      <path d="M10 0 L0 14" stroke="#1F2A44" strokeWidth="1" fill="none" />
    </svg>
  );
}

// ========== CHAT MESSAGE BUBBLE ==========
export default function ChatMessage({ msg, index, prevMsg, nextMsg }) {
  const isUser = msg.isUser;

  const tsCur  = msg.timestamp ? new Date(msg.timestamp).getTime() : 0;
  const tsPrev = prevMsg?.timestamp ? new Date(prevMsg.timestamp).getTime() : 0;
  const tsNext = nextMsg?.timestamp ? new Date(nextMsg.timestamp).getTime() : 0;

  const GROUP_GAP_MS = 60_000;
  const sameDay = (a, b) => a && b && new Date(a).toDateString() === new Date(b).toDateString();
  const groupedWithPrev = !!prevMsg && prevMsg.isUser === isUser
    && tsCur && tsPrev && (tsCur - tsPrev) < GROUP_GAP_MS
    && sameDay(tsCur, tsPrev);
  const groupedWithNext = !!nextMsg && nextMsg.isUser === isUser
    && tsCur && tsNext && (tsNext - tsCur) < GROUP_GAP_MS
    && sameDay(tsCur, tsNext);
  const isLastInGroup = !groupedWithNext;

  let timeLabel = null;
  if (isLastInGroup && msg.timestamp) {
    try { timeLabel = format(parseISO(msg.timestamp), "HH:mm"); }
    catch { timeLabel = null; }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.02, 0.1) }}
      className={`${isUser ? "ml-8" : "mr-4"} ${groupedWithPrev ? "mt-0.5" : "mt-3"}`}
      data-testid={`chat-message-${isUser ? "user" : "assistant"}`}
    >
      <div className={`relative p-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? "bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan"
          : "bg-nectar-card border border-nectar-border text-slate-300"
      }`}>
        {/* REFINO-pos-ec7fb7d (D5): assistant text renderiza markdown
            (bold/lista/etc); user text mantém texto puro. agent_loop
            às vezes emite "**R$ 1.500,00**" — antes aparecia literal. */}
        {isUser ? <div>{msg.text}</div> : _renderMessageText(msg.text)}

        {(timeLabel || (isUser && isLastInGroup && msg.status)) && (
          <div
            className={`flex items-center justify-end gap-1 mt-1 text-[10px] leading-none ${
              isUser ? "text-brand-cyan/60" : "text-slate-500"
            }`}
            data-testid="chat-message-meta"
          >
            {timeLabel && <span>{timeLabel}</span>}
            {isUser && <StatusIcon status={msg.status} />}
          </div>
        )}

        {isLastInGroup && <Tail isUser={isUser} />}
      </div>

      {!isUser && msg.actions?.length > 0 && (
        <div className="mt-2 space-y-2">
          {msg.actions.map((action, ai) => (
            <ActionDataCard key={ai} action={action} />
          ))}
        </div>
      )}

      {/* DATACARDS-TEMPLATES — visual inline pra perguntas de consulta */}
      {!isUser && msg.datacard && (
        <MessageDataCard datacard={msg.datacard} />
      )}
    </motion.div>
  );
}
